import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb} from 'common/appInterface';

/** 카드발급신청 추천 직원(초대코드) 입력 — 고객정보(카드 비밀번호) 다음 단계 */
const ProposerInfoPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();

	const screenId = "APPL28";
	const screenName = t("APPL28.title", "Enter Invite Code");

	const [cardApply, setCardApply] = useState<Partial<CardApplyStep5>>({
		...location.state.apply,
		// 초기값(이전 입력값 또는 초대 링크 inviterCode)은 앞뒤 공백만 제거
		proposerId: (location.state.apply.proposerId ?? stringUtils.nvlDefault(location.state.query.inviterCode)).trim()
	});
	const [isClick, setClick] = useState(false);

	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) — 카드 비밀번호 입력으로 이동 */
	const callbackBackkeyEvent = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	/** 직접 입력 — 이번에 새로 입력된 부분의 공백만 제거 (붙여넣기로 들어온 중간 공백은 유지) */
	const handleProposerIdChange = (value: string) => {
		const prevValue = cardApply.proposerId ?? "";

		// 이전 값과 같은 앞부분·뒷부분을 제외한 나머지가 새로 입력된 부분
		let start = 0;
		while (start < prevValue.length && start < value.length && prevValue[start] === value[start]) {
			start++;
		}
		let end = 0;
		while (end < prevValue.length - start && end < value.length - start && prevValue[prevValue.length - 1 - end] === value[value.length - 1 - end]) {
			end++;
		}

		const filteredValue = value.slice(0, start) + value.slice(start, value.length - end).replace(/\s/g, "") + value.slice(value.length - end);

		setCardApply((prevState) => ({
			...prevState,
			proposerId: filteredValue,
		}));
	};

	/** 붙여넣기 — 붙여넣은 값의 앞뒤 공백을 제거해 커서 위치에 넣는다 */
	const handleProposerIdPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		const prevValue = cardApply.proposerId ?? "";
		const selectionStart = e.currentTarget.selectionStart ?? prevValue.length;
		const selectionEnd = e.currentTarget.selectionEnd ?? prevValue.length;
		const pastedValue = prevValue.slice(0, selectionStart) + e.clipboardData.getData("text").trim() + prevValue.slice(selectionEnd);

		setCardApply((prevState) => ({
			...prevState,
			proposerId: pastedValue,
		}));
	};

	/** 입력값 전체 삭제 (X 버튼) */
	const clickClearProposerId = () => {
		setCardApply((prevState) => ({
			...prevState,
			proposerId: "",
		}));
	};

	/** Next 처리 */
	const clickNext = useCallback(() => {
		if (isClick) {
			console.log("Already Clicked...");
			return;
		}

		setClick(true);
		showProgressWeb();

		// 카드신청내역 등록
		client.patch("/api/app/apply/history", {
			...location.state.query,
			receiveSeq: cardApply.receiveSeq,
			receiveDate: cardApply.receiveDate,
			receiveStatusCode: "06",
			userEngName: cardApply.userEngName,
			proposerId: cardApply.proposerId,
			cardProductCode: cardApply.cardProductCode
		}).then(() => {
			navigate("/apply/step6/method", {
				state: {
					...location.state,
					apply: {...cardApply, isChangeDelivery: false, isContinue: false}
				}
			});
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
			setClick(false);
		});
	}, [isClick, location.state, cardApply, navigate]);

	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName);
		// 하단바 숨기기
		footerShowEventWeb("N");

		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);

	return (
		<section className="pdb70">
			<div id="wrap">
				<div id="container" className="pcHidden">
					<section className="contents inviteCode">
						<h2 className="heading">{t("APPL28.explan01", "Do you have an invite code?")}</h2>
						{/* Invite Code */}
						<div className="inputField">
							<p className="label">{t("APPL28.header01", 'Invite code (optional)')}</p>
							<label className="inputBox">
								<input type="text" value={cardApply.proposerId} placeholder={t("APPL28.placeholder01", "Enter invite code")}
									   onChange={(e) => handleProposerIdChange(e.target.value)}
									   onPaste={handleProposerIdPaste}
								/>
								{/* 입력값이 있을 때만 지우기 버튼 노출 (누를 때 입력 포커스 유지) */}
								{
									!stringUtils.checkValueNull(cardApply.proposerId) &&
										<button type="button" className="clear" onMouseDown={(e) => e.preventDefault()} onClick={clickClearProposerId}>
											<span className="blind">delete</span>
										</button>
								}
							</label>
							<div className="hint" />
						</div>
					</section>
					<div className="btnArea">
						<div className="btnWrapTy01">
							<button type="button" className="btnPrimaryTy01" disabled={isClick} onClick={clickNext}>
								<span>{t("COMMON.button.next")}</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default ProposerInfoPage;
